import { useLocation } from 'react-router-dom';
import { validateForm, EValidationType, TValidatedForm } from '../util/validation/formValidation';
import { TForgotPasswordStaffPost } from '../api/types';
import API from '../api/api';
import GlobalContext from './GlobalContext';
import { EQueryStringParam, EStaffAccountType } from '../util/constants';
import { createContext, FC, useContext, useEffect, useState } from 'react';

type TForgotPasswordStaffContext = {
    forgotPasswordStaffUiState: EForgotPasswordStaffUiState;
    email: string;
    setEmail: (email: string) => void;
    emailIsValid: boolean;
    submitForgotPasswordForm: () => void;
    errorMessage: string;
};

export enum EForgotPasswordStaffUiState {
    default,
    ready,
    loading,
    error,
    retry,
    fatalError,
    success,
}

const ForgotPasswordStaffContext = createContext<TForgotPasswordStaffContext | undefined>(undefined);

export const ForgotPasswordStaffProvider: FC = ({ children }) => {
    const {
        gatewayAPIBaseUrl,
        customerAccountId,
        setCustomerAccountId,
        subscriberId,
        language,
        setStaffAccountType,
        putFullLoginLinkInLocalStorage,
    } = useContext(GlobalContext);
    const { search: queryParams } = useLocation();
    const [forgotPasswordStaffUiState, setForgotPasswordStaffUiState] = useState(EForgotPasswordStaffUiState.default);
    const [email, setEmail] = useState('');
    const [emailIsValid, setEmailIsValid] = useState(true);
    const [errorMessage, setErrorMessage] = useState(language.validation.genericError);

    const parseQueryString = () => {
        const params = new URLSearchParams(queryParams);
        if (!customerAccountId) {
            const customerAccountId = params.get(EQueryStringParam.customerAccountId);
            if (customerAccountId) {
                setCustomerAccountId(customerAccountId);
            } else {
                setForgotPasswordStaffUiState(EForgotPasswordStaffUiState.fatalError);
                return;
            }
        }
        if (params.get(EQueryStringParam.retry)) {
            setForgotPasswordStaffUiState(EForgotPasswordStaffUiState.retry);
            return;
        }
        setForgotPasswordStaffUiState(EForgotPasswordStaffUiState.ready);
    };

    const validateForgotPasswordForm = (): TValidatedForm => {
        const validatedForm = validateForm({
            email: {
                value: email,
                validationType: EValidationType.email,
            },
        });
        setEmailIsValid(validatedForm.validatedItems.email);
        return validatedForm;
    };

    const submitForgotPasswordForm = async () => {
        const validatedForm = validateForgotPasswordForm();
        if (validatedForm.isValid) {
            const postObject: TForgotPasswordStaffPost = {
                email,
                customerAccountId,
                ...(subscriberId && { subscriberId }),
            };
            try {
                setForgotPasswordStaffUiState(EForgotPasswordStaffUiState.loading);
                await API.post.forgotPasswordStaff(gatewayAPIBaseUrl, postObject);
                setForgotPasswordStaffUiState(EForgotPasswordStaffUiState.success);
            } catch (error) {
                setErrorMessage(language.validation.genericError);
                if (error.response && error.response.status === 429) {
                    setErrorMessage(language.login.error.loginLocked);
                }
                setForgotPasswordStaffUiState(EForgotPasswordStaffUiState.error);
            }
        }
    };

    useEffect(() => {
        setStaffAccountType(EStaffAccountType.account);
        parseQueryString();
    }, []);

    useEffect(() => {
        if (forgotPasswordStaffUiState === EForgotPasswordStaffUiState.success) {
            // local storage used to retrieve login link after reset password
            putFullLoginLinkInLocalStorage();
        }
    }, [forgotPasswordStaffUiState]);

    return (
        <ForgotPasswordStaffContext.Provider
            value={{
                forgotPasswordStaffUiState,
                email,
                emailIsValid,
                setEmail,
                submitForgotPasswordForm,
                errorMessage,
            }}
        >
            {children}
        </ForgotPasswordStaffContext.Provider>
    );
};

export default ForgotPasswordStaffContext;
